import { call, fork, put, take, takeLatest } from "redux-saga/effects";
import axios from "axios";
import { FileTypeRes, productsItems, ProductsResponse } from "@type/products";
import { sendTrackEvent, TRACK_EVENT } from "@util/gtm";
import {
  addorRemoveNativeAppFavoriteItems,
  searchNativeFavoriteItems,
  isGUNativeApp,
  addFavoriteStyles,
  deleteFavoriteStyles,
  searchFavoriteStyles
} from "@util/nativeApp";
import { chunk, intersection, uniq } from "lodash";

import type {
  GetProductsArgs,
  Point,
  L2,
  Response,
  SuccessfulResponse,
  StylingAbbrType
} from "@type/index";
import {
  CATEGORY_TYPE,
  GetOutfitArgs,
  OutfitDetail,
  OutfitQueryParams,
  OutfitResult,
  ProductDetail,
  ProductDetailGrid,
  VisualSearchArgs
} from "@type/index";
import {
  FETCH_FAVORITES_SEARCH,
  FETCH_OUTFIT_FAVORITES_SEARCH,
  SET_OUTFIT_FAVORITE
} from "@redux/reducer/searchResult";
import { SEND_UPLOADED_IMAGE } from "@redux/reducer/feedback";
import {
  POST_FAVORITES_ITEM,
  DELETE_FAVORITES_ITEM,
  FETCH_PRODUCT_SKU_KEY,
  FETCH_PRODUCT_L2S_WITH_IMAGE,
  GET_FAVORITES_ITEM
} from "@redux/reducer/favoritesItem";
import {
  changeFileType,
  getBrand,
  getEcRenewalImageRatio,
  getRegionFromUrl,
  transformBrandForGoogleAnalytics
} from "@util/common";
import {
  addFavorites,
  addOutfitFavorites,
  delFavorites,
  delOutfitFavorites,
  getFavoriteStatus,
  getOutfit,
  getOutfitFavoriteStatus,
  getProduct,
  getVisualSearch,
  postVisualSearchEmptyResult,
  getProductSkus,
  getProductL2SkusWithImage
} from "@redux/services";
import store from "@redux/store";
import { isEUCountry } from "@util/country";
import { getUA } from "react-device-detect";
import { FR_UserAgent } from "@util/config";
import {
  AppActions,
  FETCH_OUTFITS,
  FETCH_PRODUCTS,
  FETCH_VISUAL_SERCH,
  SearchResultActions,
  FeedbackActions,
  FavoritesItemActions
} from "../reducer";
import { HeightOptions } from "../../component/App/SearchResult/genderHeightConfig";

export function* visualSearchSaga(payload: VisualSearchArgs) {
  const similarProductsDict: any = {};
  const similarProductsDictKeys: any = [];
  // The value of the initial search result item
  try {
    const initialData = {
      aggregations: {
        colors: [],
        flags: [],
        plds: [],
        prices: [],
        ratings: [],
        sizes: [],
        tree: {}
      },
      pagination: {
        count: 0
      },
      items: [] as ProductDetailGrid[]
    };
    yield put(SearchResultActions.fetchProductsSuccess(initialData));
    yield put(SearchResultActions.fetchProductsSuccessForGa(initialData));
    yield put(SearchResultActions.setItemLoadingStatus(true));
    yield put(SearchResultActions.clearOutfits());
    yield put(SearchResultActions.setOutfitLoadingStatus(true));
    const changeFileRes: FileTypeRes = yield call(
      changeFileType,
      payload.image
    );
    const formData = new FormData();
    yield put(FeedbackActions.setImageFileData(changeFileRes));
    formData.append("image_file", changeFileRes);
    formData.append("min_score", payload.minScore);
    formData.append("result_limit", "100");
    formData.append("f_category", "true");
    // clear bounds
    yield fork(setBounds, []);
    const res: ProductsResponse = yield call(getVisualSearch, formData);
    yield fork(setVisualSearchReqResult, res);
    // init product error
    yield put(SearchResultActions.setChangedItemFilterSortError(false));
    // init outfit error
    yield put(SearchResultActions.setChangedOutfitFilterSortError(false));
    if (res.result && res.result.items && res.result.items.length > 0) {
      const sortByVerticeTop = (currentVal: any, nextVal: any) => {
        let currentValTotalY = 0;
        let nextValTotalY = 0;
        currentVal.bounding_poly.normalized_vertices.forEach(
          (vertice: Point) => {
            currentValTotalY += vertice.y;
          }
        );
        nextVal.bounding_poly.normalized_vertices.forEach((vertice: Point) => {
          nextValTotalY += vertice.y;
        });
        const currentValAverageY =
          currentValTotalY /
          currentVal.bounding_poly.normalized_vertices.length;
        const nextValAverageY =
          nextValTotalY / nextVal.bounding_poly.normalized_vertices.length;
        return currentValAverageY - nextValAverageY;
      };
      const items = res.result.items.filter(
        (item) => item.similar_products && item.similar_products.length > 0
      );
      // GA no similar products
      const noSimilarProducts = res.result.items.filter(
        (item) => !item.similar_products || item.similar_products.length === 0
      );
      noSimilarProducts.forEach(() => {
        sendTrackEvent({
          brand: transformBrandForGoogleAnalytics(getBrand()),
          event: TRACK_EVENT,
          eventAction: "Search Term",
          eventCategory: "IS_No_Result",
          eventLabel: "NoSimilarly"
        });
      });
      let totalProducts: any[] = [];
      // let productColors: string[] = [];
      items.forEach((item: any, index) => {
        const itemKey = `${
          item.localized_info.name
        }-${item.localized_info.score.toString()}`;
        const key = Symbol.for(itemKey);
        similarProductsDictKeys.push(key);
        similarProductsDict[key] = {};
        similarProductsDict[key].index = index;
        similarProductsDict[key].products = item.similar_products;
        similarProductsDict[key].ids = uniq(
          item.similar_products.map(
            (product: productsItems) => `${product.upstream_product_id}`
          )
        );
        const productIds = item.similar_products.map(
          (product: productsItems) => product.upstream_product_id
        );
        totalProducts = totalProducts.concat(productIds);
      });
      totalProducts = chunk(uniq(totalProducts), 100);
      // 因为接口有limited 限制 一次请求最多获取100条产品
      // 所以分批次获取如果相似产品productId 大于100
      let productItems: productsItems[] = [];
      for (const [, funItems] of totalProducts.entries()) {
        const response: SuccessfulResponse = yield call(getProduct, {
          productIds: funItems,
          aggregations: ["categories", "sizes"],
          limit: 100,
          imageRatio: getEcRenewalImageRatio()
        });
        yield fork(setProductReqResult, response);
        productItems = productItems.concat(response.result.items);
      }
      const hasProductItemIds = productItems.map((item: any) => item.productId);
      similarProductsDictKeys.forEach((key: any) => {
        const index = similarProductsDict[key].index;
        // BFF返回的有产品的列表和请求的相似产品求交集
        // * 有交集 ->代表该相似产品列表中有产品展示
        // * 没有交集 ->代表该相似产品列表中没有产品展示
        const isHasSameProduct = () =>
          intersection(hasProductItemIds, similarProductsDict[key].ids).length >
          0;
        items[index].hasProduct = isHasSameProduct();
      });
      // remove duplicate
      const resultItems = items
        // filter in stock items
        .filter((item: any) => item.hasProduct)
        // clear no similar products
        .filter(
          (item: any) =>
            item.similar_products && item.similar_products.length > 0
        )
        // sort by name
        .sort((a, b) => sortByVerticeTop(a.localized_info, b.localized_info))
        .map((item: any) => {
          const newItem = JSON.parse(JSON.stringify(item));
          // Remove duplicate
          const duplicate = new Map();
          newItem.similar_products = newItem.similar_products.filter(
            (a: any) =>
              !duplicate.has(a.upstream_product_id) &&
              duplicate.set(a.upstream_product_id, 1)
          );
          return newItem;
        });
      const bounds: any[] = resultItems
        .map((item, index) => ({
          ...item.localized_info,
          selectedName: `name-${item.localized_info.name}-${index}`
        }))
        .sort(sortByVerticeTop);
      let defaultIndex = 0;
      // find index from cache tag name
      if (payload.tagName && bounds.length > 0) {
        const tagIndex = bounds.findIndex(
          (bound: any) => bound.selectedName === payload.tagName
        );
        if (tagIndex > -1) {
          defaultIndex = tagIndex;
        }
      }
      const products: productsItems[] =
        resultItems.length > 0
          ? resultItems[defaultIndex].similar_products || []
          : [];
      const productsIds = products.map(
        (product: productsItems) => product.upstream_product_id
      );
      // _devDebuggerDotScope(res);
      yield fork(setProductIds, productsIds);
      yield fork(setBounds, bounds);
      const newResult = resultItems.map((item: any, index: number) => ({
        ...item,
        selectedName: `name-${item.localized_info.name}-${index}`
      }));
      yield fork(setVisualProducts, newResult);
      if (productsIds.length === 0) {
        yield put(SearchResultActions.setItemLoadingStatus(false));
        yield fork(setProductIds, []);
        yield fork(setBounds, []);
        yield put(SearchResultActions.setOutfitLoadingStatus(false));
        yield fork(getOutfits, { payload: { productIds: [] } });
      }
      payload.callback(productsIds, products);
    } else {
      yield put(SearchResultActions.setItemLoadingStatus(false));
      yield fork(setVisualProducts, []);
      yield fork(setProductIds, []);
      yield fork(setBounds, []);
      yield put(SearchResultActions.setOutfitLoadingStatus(false));
      yield fork(getOutfits, { payload: { productIds: [] } });
      sendTrackEvent({
        brand: transformBrandForGoogleAnalytics(getBrand()),
        event: TRACK_EVENT,
        eventAction: "Search Term",
        eventCategory: "IS_No_Result",
        eventLabel: "NotRecognized"
      });
      payload.callback([], []);
    }
    // eslint-disable-next-line no-empty
  } catch (e) {}
}

export function* setProductIds(productsIds: string[]) {
  yield put(SearchResultActions.setProductIds(productsIds));
}

export function* setBounds(bounds: any[]) {
  yield put(SearchResultActions.setBounds(bounds));
}

export function* setVisualProducts(products: any[]) {
  yield put(SearchResultActions.setVisualProducts(products));
}

export function* getProducts(action: { payload: GetProductsArgs }) {
  try {
    const {
      productsIds,
      path = [],
      sizeCodes = [],
      limit = 100,
      sort = -1,
      callback
    } = action.payload;
    yield put(SearchResultActions.clearOutfits());
    if (productsIds.length > 0) {
      // init product error
      yield put(SearchResultActions.setChangedItemFilterSortError(false));
      yield put(SearchResultActions.setProductsNoData(false));
      yield put(SearchResultActions.setItemLoadingStatus(true));
      yield put(SearchResultActions.setOutfitLoadingStatus(true));
      let params: any = {
        productIds: productsIds,
        aggregations: ["categories", "sizes"],
        limit,
        imageRatio: getEcRenewalImageRatio()
      };
      if (path.length) {
        const keys = ["genderIds", "classIds", "categoryIds", "subCategoryIds"];
        const obj: Record<string, number[]> = {};
        path.forEach((value, index) => {
          if (
            index < keys.length &&
            value !== undefined &&
            value !== null &&
            value !== ""
          ) {
            const numValue = parseInt(value, 10);
            if (!isNaN(numValue)) {
              obj[keys[index]] = [numValue];
            }
          }
        });
        params = { ...params, ...obj };
      }
      if (sizeCodes.length) {
        params = { ...params, sizeCodes: sizeCodes };
      }
      // set params of params
      /**
       * if sort===1  sort by New
       * if params doesn't have sort.  sort by Similarity
       */
      if (sort === 1) {
        params = { ...params, sort: 1 };
      }
      // delete sort of params
      if (sort < 1) {
        delete params.sort;
      }
      const res: SuccessfulResponse = yield call(getProduct, { ...params });
      if (res.result && res.result.items && res.result.items.length > 0) {
        const l2Ids = res.result.items
          .map((item: ProductDetail) => item.representative.l2Id)
          .filter((item: string) => item);
        yield fork(getFavoritesSearch, { result: res.result, l2Ids });
      } else {
        yield put(SearchResultActions.setItemLoadingStatus(false));
        yield put(SearchResultActions.setOutfitLoadingStatus(false));
        const defaultResult: OutfitResult = {
          offset: 0,
          similarOutfits: [],
          total: 0
        };
        yield put(SearchResultActions.fetchOutfitsSuccess(defaultResult));
        yield put(SearchResultActions.fetchProductsSuccess(res.result));
        yield put(SearchResultActions.fetchProductsSuccessForGa(res.result));
      }
      // 修改filter category type
      let categoryType: string;
      switch (path.length) {
        case 0:
          categoryType = CATEGORY_TYPE.GENDERS;
          break;
        case 1:
          categoryType = CATEGORY_TYPE.CLASSES;
          break;
        case 2:
          categoryType = CATEGORY_TYPE.CATEGORYS;
          break;
        case 3:
          categoryType = CATEGORY_TYPE.SUB_CATEGORIES;
          break;
        default:
          categoryType = "";
          break;
      }
      if (callback) {
        callback(
          res.result.aggregations.sizes,
          Object.values(res.result.aggregations.tree).flat(1),
          res.result,
          categoryType
        );
      }
    } else {
      yield put(SearchResultActions.setItemLoadingStatus(false));
      yield put(SearchResultActions.setOutfitLoadingStatus(false));
      yield put(SearchResultActions.setProductsNoData(true));
    }
    // eslint-disable-next-line no-empty
  } catch (e) {}
}

export function* getOutfits(action: { payload: GetOutfitArgs }) {
  // init outfit error
  yield put(SearchResultActions.setChangedOutfitFilterSortError(false));
  try {
    const {
      productIds,
      gender = 0,
      height = "",
      offset = 0,
      limit = 25,
      region = "jp",
      language = "ja",
      callback
    } = action.payload;
    if (productIds.length > 0) {
      const params: OutfitQueryParams = {
        productId: productIds.join(","),
        offset,
        limit,
        unit:
          (region === "us" && language === "en") ||
          (region === "uk" && language === "en")
            ? "inch"
            : "cm",
        order: "display_start_at:desc"
      };
      // check filter
      if (gender) {
        params.genderCode = gender.toString();
        // 1.MEN 2.WOMEN 3.KIDS 4.BABY
      }
      if (height) {
        const OUTFIT_GENDER: any = {
          ALL: 0,
          MEN: 1,
          WOMEN: 2,
          KIDS: 3,
          BABY: 4,
          KIDSANDBABY: "3,4"
        };
        const totalHeight =
          HeightOptions[`${language}-${region}`] || HeightOptions["en-eu"];
        let _index = 0;
        // eslint-disable-next-line array-callback-return
        Object.keys(totalHeight).map((item, index) => {
          if (OUTFIT_GENDER[item.toLocaleUpperCase()] === gender)
            _index = index;
        });
        const currentHeight = Object.values(totalHeight)[_index];
        const res: any = currentHeight.filter((item) => item.value === height);
        const minHeight = res[0].min ? Number(res[0].min) : 0;
        const maxHeight = res[0].max ? Number(res[0].max) : 0;
        if (minHeight) {
          params.minModelHeight = minHeight;
        }
        if (maxHeight) {
          params.maxModelHeight = maxHeight;
        }
      }
      if (offset === 0) {
        yield put(SearchResultActions.setOutfitLoadingStatus(true));
      } else {
        //use load next page loading component
        yield put(SearchResultActions.setOutfitHasNextPageLoadingStatus(true));
      }
      const res: SuccessfulResponse = yield call(getOutfit, {
        params
      });
      if (res.result.similarOutfits.length < 25) {
        yield put(SearchResultActions.setOutfitHasMore(false));
      } else {
        yield put(SearchResultActions.setOutfitHasMore(true));
      }
      // EU not visible Favorite icon on outfit
      if (!isEUCountry(getRegionFromUrl())) {
        yield fork(getOutfitFavoritesSearch, { result: res.result, callback });
      } else {
        yield put(SearchResultActions.fetchOutfitsSuccess(res.result));
        if (callback) {
          callback();
        }
      }
    } else {
      const defaultResult: OutfitResult = {
        offset: 0,
        similarOutfits: [],
        total: 0
      };
      yield put(SearchResultActions.fetchOutfitsSuccess(defaultResult));
    }
    // eslint-disable-next-line no-empty
  } catch (e) {}
}

export function* getOutfitFavoritesSearch({
  result,
  callback
}: {
  result: any;
  callback?: () => void;
}) {
  try {
    const similarOutfits = result.similarOutfits;
    // TODO: Note!When the wish function is unified in the APP, the judgment here needs to be processed.
    if (isGUNativeApp() && getUA.indexOf(FR_UserAgent) <= -1) {
      // eslint-disable-next-line no-lonely-if
      if (similarOutfits.length > 0) {
        const shIds = similarOutfits.filter(
          (item: any) => item.outfitType === "stylehint"
        );
        const sbIds = similarOutfits.filter(
          (item: any) => item.outfitType === "official"
        );
        let shRes: string[] | null = null;
        let sbRes: string[] | null = null;
        if (shIds.length !== 0) {
          const { stylingIds }: { stylingIds: string[] } = yield call(
            searchFavoriteStyles,
            shIds
              .map((item: any) => item.styleId)
              .toString()
              .split(","),
            "sh"
          );
          shRes = stylingIds;
        }
        if (sbIds.length !== 0) {
          const { stylingIds } = yield call(
            searchFavoriteStyles,
            sbIds
              .map((item: any) => item.styleId)
              .toString()
              .split(","),
            "sb"
          );
          sbRes = stylingIds;
        }
        let resResult: Array<any> = [];
        if (shRes !== null && sbRes === null) {
          resResult = shRes;
        }
        if (shRes === null && sbRes !== null) {
          resResult = sbRes;
        }
        if (shRes === null && sbRes === null) {
          resResult = [];
        }
        if (shRes !== null && sbRes !== null) {
          resResult = shRes.concat(sbRes);
        }
        const outfitResults = result;
        if (resResult.length > 0) {
          resResult.forEach((styleId: string) => {
            outfitResults.similarOutfits.forEach(
              (item: OutfitDetail, index: number) => {
                if (item.styleId.toString() === styleId) {
                  outfitResults.similarOutfits[index].favorite = true;
                }
              }
            );
          });
        }
        yield put(SearchResultActions.fetchOutfitsSuccess(outfitResults));
        // Loading is shown on the first page
        if (callback) {
          callback();
        }
      } else {
        yield put(SearchResultActions.fetchOutfitsSuccess(result));
      }
    } else {
      // eslint-disable-next-line no-lonely-if
      if (similarOutfits.length > 0) {
        const shIds = similarOutfits.filter(
          (item: any) => item.outfitType === "stylehint"
        );
        const sbIds = similarOutfits.filter(
          (item: any) => item.outfitType === "official"
        );
        let shRes: Response | null = null;
        let sbRes: Response | null = null;
        if (shIds.length !== 0) {
          shRes = yield call(getOutfitFavoriteStatus, {
            params: {
              ids: shIds.map((item: any) => item.styleId).toString(),
              type: "sh"
            }
          });
        }
        if (sbIds.length !== 0) {
          sbRes = yield call(getOutfitFavoriteStatus, {
            params: {
              ids: sbIds.map((item: any) => item.styleId).toString(),
              type: "sb"
            }
          });
        }
        let resResult: Array<any> = [];
        if (shRes !== null && sbRes === null) {
          resResult = shRes.result;
        }
        if (shRes === null && sbRes !== null) {
          resResult = sbRes.result;
        }
        if (shRes === null && sbRes === null) {
          resResult = [];
        }
        if (shRes !== null && sbRes !== null) {
          resResult = shRes.result.concat(sbRes.result);
        }
        const outfitResults = result;
        if (resResult.length > 0) {
          // catch un login
          if (shRes?.status === "nok" || sbRes?.status === "nok") {
            yield put(SearchResultActions.fetchOutfitsSuccess(result));
            // yield put(SearchResultActions.fetchProductsSuccessForGa(result));
          } else {
            resResult.forEach((styleId: string) => {
              outfitResults.similarOutfits.forEach(
                (item: OutfitDetail, index: number) => {
                  if (item.styleId.toString() === styleId) {
                    outfitResults.similarOutfits[index].favorite = true;
                  }
                }
              );
            });
          }
        }
        yield put(SearchResultActions.fetchOutfitsSuccess(outfitResults));
        // Loading is shown on the first page
        if (callback) {
          callback();
        }
      } else {
        yield put(SearchResultActions.fetchOutfitsSuccess(result));
        // yield put(SearchResultActions.fetchProductsSuccessForGa(result));
      }
    }
  } catch (e) {
    // /favorites/styles/search endpoint 5xx error handling
    // don't show 500 error modal/popup
    yield put(SearchResultActions.fetchOutfitsSuccess(result));
    // yield put(SearchResultActions.fetchProductsSuccessForGa(result));
  }
}

export function* getFavoritesSearch({
  result,
  l2Ids
}: {
  result: any;
  l2Ids: string[];
}) {
  try {
    if (l2Ids.length > 0) {
      const res: Response = yield call(getFavoriteStatus, {
        params: {
          ids: l2Ids.join(",")
        }
      });
      // set product item ids for fetch outfits
      if (result.items.length > 0) {
        const itemIds = result.items.forEach(
          (item: ProductDetailGrid) => item.productId
        );
        yield put(SearchResultActions.setProductItemIds(itemIds));
      }

      // catch un login
      if (res.status === "nok") {
        yield put(SearchResultActions.fetchProductsSuccess(result));
        yield put(SearchResultActions.fetchProductsSuccessForGa(result));
      } else {
        const newResult: any = result;
        const favoritesL2Ids = Object.keys(res.result);
        if (newResult.items.length > 0) {
          newResult.items.forEach((item: ProductDetail, index: number) => {
            favoritesL2Ids.forEach((l2Id: string) => {
              if (item.representative.l2Id === l2Id) {
                newResult.items[index].favorite = res.result[l2Id];
              }
            });
          });
        }
        yield put(SearchResultActions.fetchProductsSuccess(newResult));
        yield put(SearchResultActions.fetchProductsSuccessForGa(newResult));
      }
    } else {
      yield put(SearchResultActions.fetchProductsSuccess(result));
      yield put(SearchResultActions.fetchProductsSuccessForGa(result));
    }
  } catch (e) {
    // /favorites/search endpoint 5xx error handling
    // don't show 500 error modal/popup
    if (result.items.length > 0) {
      const itemIds = result.items.forEach(
        (item: ProductDetailGrid) => item.productId
      );
      yield put(SearchResultActions.setProductItemIds(itemIds));
    }
    yield put(SearchResultActions.fetchProductsSuccess(result));
    yield put(SearchResultActions.fetchProductsSuccessForGa(result));
  }
}

export function* fetchProductSKUsSaga(payload: any) {
  try {
    const { priceGroup } = payload;
    yield put(FavoritesItemActions.fetchProductSKUsLoading(true));
    const res: Response = yield call(getProductSkus, payload, null);
    if (res.status === "ok" && res.result) {
      if (res.result.l2s) {
        const colorGroups: { [key: string]: L2[] } = {};
        res.result.l2s.forEach((l2: any) => {
          if (!colorGroups[l2.color?.code || "all"]) {
            colorGroups[l2.color?.code || "all"] = [];
          }
          colorGroups[l2.color?.code || "all"].push(l2);
        });
        yield put(FavoritesItemActions.fetchProductSKUsSuccess(colorGroups));
        if (res.result.images) {
          yield put(
            FavoritesItemActions.fetchProductSKUsImagesSuccess(
              res.result.images
            )
          );
        }
      }
      if (res.result.wishlist) {
        const updatedWishlist = res.result.wishlist.map((wishlist: any) => ({
          ...wishlist,
          priceGroup
        }));
        yield put(FavoritesItemActions.getWishlistSuccess(updatedWishlist));
      }
      yield put(FavoritesItemActions.fetchProductSKUsLoading(false));
      yield put(FavoritesItemActions.fetchProductSKUsFail(false));
    } else if (res.error && res.error.httpStatusCode === 401) {
      yield put(FavoritesItemActions.fetchProductSKUsLoading(false));
      yield put(FavoritesItemActions.fetchProductSKUsFail(false));
      store.dispatch(AppActions.setLoginModal(true));
    } else {
      yield put(FavoritesItemActions.fetchProductSKUsLoading(false));
      yield put(FavoritesItemActions.fetchProductSKUsFail(true));
    }
  } catch (error) {
    yield put(FavoritesItemActions.fetchProductSKUsLoading(false));
    yield put(FavoritesItemActions.fetchProductSKUsFail(true));
  }
}

// For GU APP -> fetch product skus data
// API to return all l2s of a product with CSP and image details
export function* fetchProductL2sWithImageSaga(payload: any) {
  try {
    yield put(FavoritesItemActions.fetchProductSKUsLoading(true));
    const res: Response = yield call(getProductL2SkusWithImage, payload, null);
    if (res.status === "ok" && res.result) {
      if (res.result.l2s) {
        const colorGroups: { [key: string]: L2[] } = {};
        res.result.l2s.forEach((l2: any) => {
          if (!colorGroups[l2.color?.code || "all"]) {
            colorGroups[l2.color?.code || "all"] = [];
          }
          const updatedL2 = {
            ...l2,
            isWishlisted: false // wishlist value will be fetched from mobileapp.js
          };
          colorGroups[l2.color?.code || "all"].push(updatedL2);
        });
        yield put(FavoritesItemActions.fetchProductSKUsSuccess(colorGroups));
        // get product wish list
        const l2Ids =
          (res.result.l2s.map(({ l2Id }: any) => l2Id) as string[]) ?? [];
        yield put(
          FavoritesItemActions.getFavoritesItem({
            l2Ids: l2Ids,
            isGUNativeApp: true
          })
        );
      }
      yield put(FavoritesItemActions.fetchProductSKUsLoading(false));
      yield put(FavoritesItemActions.fetchProductSKUsFail(false));
    } else {
      yield put(FavoritesItemActions.fetchProductSKUsLoading(false));
      yield put(FavoritesItemActions.fetchProductSKUsFail(true));
    }
  } catch (error) {
    yield put(FavoritesItemActions.fetchProductSKUsLoading(false));
    yield put(FavoritesItemActions.fetchProductSKUsFail(true));
  }
}

// get favorites item list
export function* getFavoritesItemSaga(payload: any) {
  try {
    yield put(FavoritesItemActions.getFavoritesItemLoading(true));
    if (payload.isGUNativeApp) {
      try {
        const { skuIds: wishlistSKUs }: { skuIds: any["items"] } = yield call(
          searchNativeFavoriteItems,
          {
            items: payload.l2Ids
          }
        );
        const updatedWishlist = wishlistSKUs.map(
          ({ l2Id, isRepresentative }: any) => ({
            l2Id,
            display: true, // TODO: check how we can get the correct value for this
            isRepresentative: isRepresentative ?? false
          })
        );
        yield put(FavoritesItemActions.getWishlistSuccess(updatedWishlist));
        yield put(FavoritesItemActions.getFavoritesItemLoading(false));
      } catch (e) {
        yield put(FavoritesItemActions.getFavoritesItemLoading(false));
      }
    }
  } catch (error) {
    yield put(FavoritesItemActions.getFavoritesItemLoading(false));
  }
}

export function* setOutfitFavorite(outfit: OutfitDetail) {
  try {
    const type = outfit.outfitType === "stylehint" ? "sh" : "sb";
    // TODO: Note!When the wish function is unified in the APP, the judgment here needs to be processed.
    if (isGUNativeApp() && getUA.indexOf(FR_UserAgent) <= -1) {
      try {
        const { status }: { status: boolean } = !outfit.favorite
          ? yield call(
              addFavoriteStyles,
              JSON.stringify(outfit.styleId),
              type as StylingAbbrType
            )
          : yield call(
              deleteFavoriteStyles,
              JSON.stringify(outfit.styleId),
              type as StylingAbbrType
            );
        if (status) {
          yield put(SearchResultActions.setOutfitFavoriteSuccess(outfit));
        }
        // eslint-disable-next-line no-empty
      } catch (e) {}
    } else {
      const res: Response = !outfit.favorite
        ? yield call(
            addOutfitFavorites,
            [
              {
                styleId: outfit.styleId.toString()
              }
            ],
            {
              params: {
                type: type
              }
            }
          )
        : yield call(delOutfitFavorites, outfit.styleId.toString(), {
            params: {
              type: type
            }
          });
      if (res.status === "ok") {
        yield put(SearchResultActions.setOutfitFavoriteSuccess(outfit));
      } else if (
        res.status === "nok" &&
        res.error &&
        res.error.httpStatusCode === 401
      ) {
        // catch un login
        yield put(AppActions.setLoginModal(true));
      } else {
        yield put(AppActions.setServerErrorModal(true));
      }
    }
    // eslint-disable-next-line no-empty
  } catch (e) {}
}

export function* postFavoritesItemSaga(payload: any) {
  try {
    yield put(FavoritesItemActions.getFavoritesItemLoading(true));
    if (payload.isGUNativeApp) {
      try {
        const { status }: { status: boolean } = yield call(
          addorRemoveNativeAppFavoriteItems,
          {
            isFavourite: true,
            items: payload.body
          }
        );
        if (status) {
          yield put(
            FavoritesItemActions.postFavoritesItemSuccess(payload.body[0].l2Id)
          );
        }
        yield put(FavoritesItemActions.getFavoritesItemLoading(false));
      } catch (e) {
        yield put(FavoritesItemActions.getFavoritesItemLoading(false));
      }
    } else {
      const res: Response = yield call(addFavorites, payload.body);
      if (res.status === "ok") {
        yield put(
          FavoritesItemActions.postFavoritesItemSuccess(payload.body[0].l2Id)
        );
      }
      yield put(FavoritesItemActions.getFavoritesItemLoading(false));
    }
  } catch (error) {
    yield put(FavoritesItemActions.getFavoritesItemLoading(false));
    if (!axios.isCancel(error)) {
      yield put(AppActions.setServerErrorModal(true));
    }
  }
}

export function* deleteFavoritesItemSaga(payload: any) {
  try {
    yield put(FavoritesItemActions.getFavoritesItemLoading(true));
    if (payload.isGUNativeApp) {
      try {
        const { status }: { status: boolean } = yield call(
          addorRemoveNativeAppFavoriteItems,
          {
            isFavourite: false,
            items: [{ l2Id: payload.id }]
          }
        );
        if (status) {
          yield put(FavoritesItemActions.delFavoritesItemSuccess(payload.id));
        }
        yield put(FavoritesItemActions.getFavoritesItemLoading(false));
      } catch (e) {
        yield put(FavoritesItemActions.getFavoritesItemLoading(false));
      }
    } else {
      const res: Response = yield call(delFavorites, payload.id);
      if (res.status === "ok") {
        yield put(FavoritesItemActions.delFavoritesItemSuccess(payload.id));
      }
      yield put(FavoritesItemActions.getFavoritesItemLoading(false));
    }
  } catch (error) {
    yield put(FavoritesItemActions.getFavoritesItemLoading(false));
    if (!axios.isCancel(error)) {
      yield put(AppActions.setServerErrorModal(true));
    }
  }
}

export function* setVisualSearchReqResult(res: any) {
  yield put(FeedbackActions.setVisualSearchReqResult(res));
}

export function* setProductReqResult(res: any) {
  yield put(FeedbackActions.setProductReqResult(res));
}

export function* sendUploadedImageSaga(payload: any) {
  try {
    const res: Response = yield call(postVisualSearchEmptyResult, payload);
    if (res && res.status === "ok") {
      yield put(FeedbackActions.setUploadedImageSuccess(true));
    } else {
      yield put(AppActions.setServerErrorModal(true));
    }
    // eslint-disable-next-line no-empty
  } catch (e) {}
}

export function* watchVisualSearchSaga(): any {
  while (true) {
    const action = yield take(FETCH_VISUAL_SERCH);
    const payload = action.payload;
    yield fork(visualSearchSaga, payload);
  }
}

export function* watchGetProductsSaga(): any {
  yield takeLatest<any>(FETCH_PRODUCTS, getProducts);
}

export function* watchGetOutfitsSaga(): any {
  yield takeLatest<any>(FETCH_OUTFITS, getOutfits);
}

export function* watchGetFavoritesSearchSaga(): any {
  while (true) {
    const action = yield take(FETCH_FAVORITES_SEARCH);
    const payload: any = action.payload;
    yield fork(getFavoritesSearch, payload);
  }
}

export function* watchGetOutfitFavoritesSearchSaga(): any {
  while (true) {
    const action = yield take(FETCH_OUTFIT_FAVORITES_SEARCH);
    const payload: any = action.payload;
    yield fork(getOutfitFavoritesSearch, payload);
  }
}

export function* watchSetOutfitFavoriteSaga(): any {
  while (true) {
    const action = yield take(SET_OUTFIT_FAVORITE);
    const payload = action.payload;
    yield fork(setOutfitFavorite, payload);
  }
}

export function* watchPostFavoritesItemSaga(): any {
  while (true) {
    const action = yield take(POST_FAVORITES_ITEM);
    const payload = action.payload;
    yield fork(postFavoritesItemSaga, payload);
  }
}

export function* watchDeleteFavouritesItemSaga(): any {
  while (true) {
    const action = yield take(DELETE_FAVORITES_ITEM);
    const payload = action.payload;
    yield fork(deleteFavoritesItemSaga, payload);
  }
}

export function* watchFetchProductSKUs(): any {
  while (true) {
    const action = yield take(FETCH_PRODUCT_SKU_KEY);
    const payload = action.payload;
    yield fork(fetchProductSKUsSaga, payload);
  }
}

export function* watchFetchProductL2WithImage(): any {
  while (true) {
    const action = yield take(FETCH_PRODUCT_L2S_WITH_IMAGE);
    const payload = action.payload;
    yield fork(fetchProductL2sWithImageSaga, payload);
  }
}

export function* watchGetFavoritesItemSaga(): any {
  while (true) {
    const action = yield take(GET_FAVORITES_ITEM);
    const payload = action.payload;
    yield fork(getFavoritesItemSaga, payload);
  }
}

export function* watchSendUploadedImageSaga(): any {
  while (true) {
    const action = yield take(SEND_UPLOADED_IMAGE);
    const payload = action.payload;
    yield fork(sendUploadedImageSaga, payload);
  }
}
